import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { apiTreeDashboard, apiTreeAttention, apiTreeNotifySup } from "./api";

// Alpha-and-above Activity Dashboard — summary cards + level chart +
// members-needing-attention list + most / least active leaderboards.
export default function AlphaActivityDashboard({ onOpenMember, currentUser }) {
  const [data, setData] = useState(null);
  const [attention, setAttention] = useState(null);
  const [notifying, setNotifying] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [d, a] = await Promise.all([apiTreeDashboard(), apiTreeAttention()]);
        setData(d);
        setAttention(a);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const notifySup = async (nodeId, memberName) => {
    setNotifying(nodeId);
    try {
      await apiTreeNotifySup(nodeId);
      setToast(`Reminder sent for ${memberName}`);
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setToast(e?.message || "Failed to send reminder");
      setTimeout(() => setToast(null), 3000);
    } finally { setNotifying(null); }
  };

  if (!data) {
    return <div data-testid="dashboard-loading" style={{ padding: 24, color: "var(--kc-sub)" }}>Loading dashboard…</div>;
  }

  const cards = data.cards || {};

  return (
    <div data-testid="alpha-dashboard" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {toast && <div data-testid="dashboard-toast" style={{ position: "fixed", top: 20, right: 20, background: "color-mix(in srgb, var(--kc-accent) 20%, transparent)", color: "var(--kc-accent)", padding: "10px 14px", borderRadius: 8, border: "1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent)", zIndex: 300 }}>{toast}</div>}

      {/* Summary cards */}
      <div data-testid="dashboard-cards" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
        <SummaryCard label="Logged this week" value={cards.logsThisWeek} testid="card-week" />
        <SummaryCard label="Logged this month" value={cards.logsThisMonth} testid="card-month" />
        <SummaryCard label="Total members" value={cards.totalMembers} testid="card-total" />
        <SummaryCard label="Vacant slots" value={cards.totalVacantSlots} testid="card-vacant" />
      </div>

      {/* Bar chart */}
      <div style={{ background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 12, padding: 16 }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 12, color: "var(--kc-accent)", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, marginBottom: 10 }}>
          Follow-ups by level · this month
        </div>
        <div style={{ width: "100%", height: 220 }} data-testid="dashboard-chart">
          <ResponsiveContainer>
            <BarChart data={data.chart || []} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="level" tick={{ fill: "rgba(255,255,255,0.75)", fontSize: 11 }} />
              <YAxis tick={{ fill: "rgba(255,255,255,0.75)", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#111", border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)", color: "var(--kc-text)" }} />
              <Bar dataKey="count" fill="var(--kc-accent)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Members needing attention */}
      <div style={{ background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 12, padding: 16 }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 12, color: "var(--kc-accent)", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, marginBottom: 10 }}>
          Needing attention {attention?.threshold ? `· threshold ${attention.threshold}d` : ""}
        </div>
        {(!attention?.items || attention.items.length === 0) ? (
          <div data-testid="attention-empty" style={{ color: "var(--kc-sub)", fontSize: 12 }}>Everyone is within the follow-up window. 🎉</div>
        ) : (
          <div data-testid="attention-list" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {attention.items.map(r => (
              <div key={r.nodeId} data-testid={`att-${r.slotRef}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "#111", borderRadius: 8, border: `1px solid ${r.health === "red" ? "rgba(255,107,107,0.35)" : "rgba(245,158,11,0.35)"}` }}>
                {r.photo ? (
                  <img src={r.photo} alt="" style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--kc-accent)", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                    {((r.memberName || "?")[0] || "?").toUpperCase()}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: "var(--kc-text)" }}>{r.memberName} <span style={{ color: "var(--kc-sub)", fontSize: 11 }}>· #{r.memberNumber} · {r.levelName}</span></div>
                  <div style={{ fontSize: 11, color: r.health === "red" ? "#ff6b6b" : "#f59e0b" }}>
                    {r.daysSinceLastContact ?? "?"} days since last contact
                  </div>
                </div>
                <button className="btn btn-outline btn-sm" data-testid={`att-notify-${r.slotRef}`} disabled={notifying === r.nodeId || !r.supervisorNodeId} onClick={() => notifySup(r.nodeId, r.memberName)}>
                  {notifying === r.nodeId ? "…" : "Notify Supervisor"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Most / least active */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
        <MiniList title="Most active this month" items={data.mostActive || []} testid="most-active" onOpenMember={onOpenMember} />
        <MiniList title="Least active this month" items={data.leastActive || []} testid="least-active" onOpenMember={onOpenMember} />
      </div>
    </div>
  );
}

function SummaryCard({ label, value, testid }) {
  return (
    <div data-testid={testid} style={{ background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 10, padding: "12px 14px" }}>
      <div style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.10em", fontWeight: 700 }}>{label}</div>
      <div style={{ fontFamily: "'Cinzel',serif", fontSize: 26, color: "var(--kc-accent)", fontWeight: 700, marginTop: 3 }}>{value ?? 0}</div>
    </div>
  );
}

function MiniList({ title, items, testid, onOpenMember }) {
  return (
    <div style={{ background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 12, padding: 14 }} data-testid={testid}>
      <div style={{ fontFamily: "'Cinzel',serif", fontSize: 12, color: "var(--kc-accent)", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, marginBottom: 10 }}>{title}</div>
      {items.length === 0 ? (
        <div style={{ color: "var(--kc-sub)", fontSize: 12 }}>No data yet.</div>
      ) : items.map((r, i) => (
        <div key={r.memberId || i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
          {r.photo ? (
            <img src={r.photo} alt="" style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
          ) : (
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: "var(--kc-accent)", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
              {((r.name || "?")[0] || "?").toUpperCase()}
            </div>
          )}
          <button onClick={() => r.memberId && onOpenMember && onOpenMember(r.memberId)} style={{ background: "none", border: "none", color: "var(--kc-text)", cursor: "pointer", flex: 1, textAlign: "left", padding: 0, fontFamily: "inherit", fontSize: 13, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name || "Unknown"}</button>
          <span style={{ color: "var(--kc-accent)", fontWeight: 700, fontSize: 13 }}>{r.count}</span>
        </div>
      ))}
    </div>
  );
}
